import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchOne as fetchBrowseHomePage, saveOne as upsertBrowseHomePage } from 'app/api/admin/browse/homePageApi';
import ConfirmUnsavedChangesModal from 'app/components/admin/browse/shared/ConfirmUnsavedChangesModal';
import ErrorPage from 'app/components/ErrorPage';
import AuthConstants from 'app/constants/AuthConstants';
import { MESSAGE_ACTION_GENERIC_ERROR } from 'app/constants/admin/browse';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { isSessionExpiredError, makeSuccessMessage } from 'app/helpers/admin/browse';
import {
  deserializeBrowseHomePage,
  hasAdminPermissions,
  makeEmptyBrowseHomePage,
  makeHomePreviewPageRoute
} from 'app/helpers/admin/browse/home';
import { readSessionStorageItem, removeSessionStorageItem, writeSessionStorageItem } from 'app/helpers/storage';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseHomeEditPage from './AdminBrowseHomeEditPage';

const AdminBrowseHomeEditPageContainer = ({ browseHomePageId, localeCode }) => {
  const apiClient = useApiClient();
  const dispatch = useDispatch();

  const { isAvailableLocaleCode } = useLocaleCodes();

  const currentUser = useSelector(selectCurrentUser);

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  const [browseHomePagePendingSave] = useState(() => {
    const saved = readSessionStorageItem('browsePendingHomeSave');
    if (saved) removeSessionStorageItem('browsePendingHomeSave');
    return saved;
  });

  const [state, setState] = useState({
    browseHomePage: null,
    browseHomePageWorkingCopy: null,
    errorMessage: '',
    hasUnsavedChanges: false,
    isFetching: true,
    isSaving: false,
    isPendingAutoSave: false
  });

  const {
    browseHomePage,
    browseHomePageWorkingCopy,
    errorMessage,
    hasUnsavedChanges,
    isFetching,
    isSaving,
    isPendingAutoSave
  } = state;

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (browseHomePageId) {
          return fetchBrowseHomePage(apiClient, localeCode, browseHomePageId);
        }

        return Promise.resolve(makeEmptyBrowseHomePage());
      })
      .then(deserializeBrowseHomePage)
      .then(fetchedBrowseHomePage => {
        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: fetchedBrowseHomePage,
            browseHomePageWorkingCopy: browseHomePagePendingSave || cloneDeep(fetchedBrowseHomePage),
            hasUnsavedChanges: !!browseHomePagePendingSave,
            isPendingAutoSave: !!browseHomePagePendingSave
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);

        setState(prevState => {
          return {
            ...prevState,
            errorMessage: `Failed to load home page ${browseHomePageId}`
          };
        });
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isFetching: false
          };
        });
      });
  }, [apiClient, browseHomePageId, browseHomePagePendingSave, currentUser, dispatch, localeCode, subdomain]);

  const saveBrowseHomePage = () => {
    browseHomePageWorkingCopy.updatedAt = Date.now();
    browseHomePageWorkingCopy.updatedBy = currentUser.get('name');

    setState(prevState => {
      return {
        ...prevState,
        hasUnsavedChanges: false,
        isSaving: true
      };
    });

    upsertBrowseHomePage(apiClient, localeCode, browseHomePageWorkingCopy)
      .then(savedBrowseHomePage => {
        dispatch(makeSuccessMessage('Home page saved'));

        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: savedBrowseHomePage,
            browseHomePageWorkingCopy: cloneDeep(savedBrowseHomePage)
          };
        });
      })
      .catch(err => {
        if (isSessionExpiredError(err)) {
          writeSessionStorageItem('browsePendingHomeSave', browseHomePageWorkingCopy);
          dispatch({ type: AuthConstants.OPEN_LOGIN_DIALOG, payload: {} });
        } else {
          dispatch(MESSAGE_ACTION_GENERIC_ERROR);
          /* eslint-disable-next-line no-console */
          console.error(err.message);
        }
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  useEffect(() => {
    if (isPendingAutoSave && !isFetching && currentUser) {
      setState(prevState => ({ ...prevState, isPendingAutoSave: false }));
      saveBrowseHomePage();
    }
  }, [currentUser, isFetching, isPendingAutoSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const handleWorkingCopyChange = newBrowseHomePageWorkingCopy => {
    const newHasUnsavedChanges = !isEqual(newBrowseHomePageWorkingCopy, browseHomePage);

    setState(prevState => {
      return {
        ...prevState,
        browseHomePageWorkingCopy: newBrowseHomePageWorkingCopy,
        hasUnsavedChanges: newHasUnsavedChanges
      };
    });
  };

  if (!isAvailableLocaleCode(localeCode)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }
  if (!(errorMessage || isFetching || browseHomePage)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const previewPageUrl = makeHomePreviewPageRoute(localeCode, browseHomePageId);

  return (
    <>
      <ConfirmUnsavedChangesModal hasUnsavedChanges={hasUnsavedChanges} />
      <AdminBrowseHomeEditPage
        browseHomePage={browseHomePageWorkingCopy}
        errorMessage={errorMessage}
        hasUnsavedChanges={hasUnsavedChanges}
        isFetching={isFetching}
        isSaving={isSaving}
        localeCode={localeCode}
        onChange={handleWorkingCopyChange}
        onSave={saveBrowseHomePage}
        previewPageUrl={previewPageUrl}
      />
    </>
  );
};

AdminBrowseHomeEditPageContainer.propTypes = {
  browseHomePageId: PropTypes.string,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
};

export default AdminBrowseHomeEditPageContainer;
